import React from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import AccountSettingsModal from '../AccountSettingsModal';
import AdminDashboard from '../AdminDashboard';
import SavedLessonsLibrary from '../SavedLessonsLibrary';
import QuizModal from '../QuizModal';
import GlobalAssistantDrawer from '../GlobalAssistantDrawer';

const TeacherWorkspaceOverlays = ({
    showAccountSettings,
    onCloseAccountSettings,
    userProfile,
    onUpdateProfile,
    showAdminDashboard,
    isAdmin,
    onCloseAdminDashboard,
    showSavedLessons,
    onCloseSavedLessons,
    onOpenSavedLesson,
    quizData,
    showQuizModal,
    onCloseQuizModal,
    onSaveQuiz,
    toast,
    showLogoutConfirm,
    onCloseLogoutConfirm,
    onConfirmLogout,
    isLoading,
    lessonProgressMsg,
    showAssistantDrawer,
    onCloseAssistantDrawer,
    selectedSubject,
    gradeLevel,
    userRole,
}) => {
    return (
        <>
            <AccountSettingsModal
                isOpen={showAccountSettings}
                onClose={onCloseAccountSettings}
                userProfile={userProfile}
                onUpdateProfile={onUpdateProfile}
            />

            {showAdminDashboard && isAdmin && (
                <AdminDashboard
                    onClose={onCloseAdminDashboard}
                    onNavigateToFeedback={() => {
                        onCloseAdminDashboard();
                        window.history.pushState({}, '', '/admin/feedback');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                />
            )}

            <SavedLessonsLibrary
                isOpen={showSavedLessons}
                onClose={onCloseSavedLessons}
                userProfile={userProfile}
                onOpenLesson={onOpenSavedLesson}
            />

            <QuizModal
                quiz={quizData}
                isOpen={showQuizModal}
                onClose={onCloseQuizModal}
                onSave={onSaveQuiz}
            />

            <AnimatePresence>
                {toast.visible && (
                    <Motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] font-medium text-sm flex items-center gap-2 ${toast.type === 'error' ? 'bg-red-600 text-white' :
                            toast.type === 'info' ? 'bg-[#333] text-white' :
                                'bg-emerald-600 text-white'
                            }`}
                    >
                        {toast.type === 'success' && <Check size={16} />}
                        {toast.type === 'error' && <X size={16} />}
                        {toast.message}
                    </Motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showLogoutConfirm && (
                    <Motion.div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onCloseLogoutConfirm}
                    >
                        <Motion.div
                            className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">Sign Out?</h3>
                            <p className="text-[#666] text-sm mb-6">Are you sure you want to sign out of your account?</p>
                            <div className="flex gap-3">
                                <button
                                    onClick={onCloseLogoutConfirm}
                                    className="flex-1 px-4 py-2.5 bg-[#f5f5f7] text-[#444] rounded-xl font-medium hover:bg-[#eee] transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirmLogout}
                                    className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
                                >
                                    Sign Out
                                </button>
                            </div>
                        </Motion.div>
                    </Motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isLoading && (
                    <Motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
                        style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
                    >
                        <Motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="bg-white/95 rounded-3xl p-10 shadow-[0_12px_40px_rgba(0,0,0,0.12)] flex flex-col items-center gap-5 max-w-sm mx-4"
                        >
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                                <Loader2 className="animate-spin text-white" size={28} />
                            </div>
                            <Motion.p
                                key={lessonProgressMsg}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.3 }}
                                className="text-lg font-semibold text-[#444] text-center"
                            >
                                {lessonProgressMsg}
                            </Motion.p>
                            <p className="text-sm text-[#999]">Aligning objectives · Selecting engagement · Structuring activities</p>
                        </Motion.div>
                    </Motion.div>
                )}
            </AnimatePresence>

            <GlobalAssistantDrawer
                isOpen={showAssistantDrawer}
                onClose={onCloseAssistantDrawer}
                userProfile={userProfile}
                selectedSubject={selectedSubject}
                gradeLevel={gradeLevel}
                userRole={userRole}
            />
        </>
    );
};

export default TeacherWorkspaceOverlays;
